import React, { ComponentType, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import type { SliderProps } from './SwiperSlider';
import SliderArrows from './SliderArrows';
import SliderArrowsSmall from './SliderArrowsSmall';

// Swiper is expensive to start: it measures the page and forces a layout. Most
// carousels sit below the fold, so until one comes within a screen height of
// the viewport, render the same markup Swiper renders on the server and leave
// Swiper unloaded. The switch happens off screen, so nothing visibly moves.
export default function Slider(props: SliderProps) {
  const {
    hideArrow,
    smallArrow,
    addShadow,
    paginationDots,
    className = '',
    arrowClassName = '',
    children,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const nextRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [SwiperSlider, setSwiperSlider] =
    useState<ComponentType<SliderProps>>();

  useEffect(() => {
    const container = containerRef.current;

    if (SwiperSlider || !container) {
      return undefined;
    }

    let cancelled = false;

    const load = () => {
      import('./SwiperSlider')
        .then(({ default: Component }) => {
          if (!cancelled) {
            // Wrapped in a function so React stores the component instead of
            // calling it as a state updater.
            setSwiperSlider(() => Component);
          }
        })
        // The static slides still show, so a failed chunk load only means
        // this carousel can't be swiped until the page reloads.
        .catch(() => undefined);
    };

    if (typeof IntersectionObserver === 'undefined') {
      load();

      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '100% 0px' },
    );

    observer.observe(container);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [SwiperSlider]);

  if (SwiperSlider) {
    return <SwiperSlider {...props} />;
  }

  return (
    <div ref={containerRef}>
      <div
        className={cn('swiper', className, {
          '!pb-12': paginationDots,
        })}
      >
        <div className="swiper-wrapper">
          {React.Children.map(children, (child, i) => {
            if (child) {
              return (
                <div
                  className={cn('swiper-slide', {
                    'pb-[2.2rem]': addShadow,
                  })}
                  key={`slider-key-${child}-${i}`}
                >
                  {child}
                </div>
              );
            }

            return null;
          })}
        </div>
        {paginationDots && <div className="swiper-pagination"></div>}
      </div>
      {!smallArrow && !hideArrow && (
        <SliderArrows
          prevRef={prevRef}
          nextRef={nextRef}
          className={arrowClassName}
        />
      )}
      {smallArrow && <SliderArrowsSmall prevRef={prevRef} nextRef={nextRef} />}
    </div>
  );
}
